import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import resourcesAPI from 'api/resources';
import { RejectWithMessage, TableDataResponse } from 'features/types';
import { HubPostsState, PublicJobBoardResourceTableView, GetPublicJobBoardResourcesTableFilters } from './types';
import { getErrorMessage } from 'utils/error';
import { RootState } from 'app/root-reducer';
import { Paging } from 'api/types';

export const getPublicJobBoardResource = createAsyncThunk<PublicJobBoardResourceTableView, string, RejectWithMessage>(
  'hub-posts/get',
  async (resourceId, { rejectWithValue }) => {
    try {
      return await resourcesAPI.getResource(resourceId);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const getHubPosts = createAsyncThunk<TableDataResponse<PublicJobBoardResourceTableView>, void, RejectWithMessage>(
  'hub-posts/get-table',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState();
      return await resourcesAPI.getHubPosts(state.hubPosts.requestData);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

const initialState: HubPostsState = {
  data: {
    tableDataIsLoading: false,
    tableData: {
      rows: [],
      totalCount: 0,
      noResult: false,
    },
    selectedResource: null,
  },
  requestData: {
    paging: {
      page: 1,
      pageSize: 20,
    },
    filters: {},
  },
  status: 'idle',
  error: null,
};

const hubPostsSlice = createSlice({
  name: 'hubPosts',
  initialState,
  reducers: {
    resetRequestData: (state) => {
      state.requestData = { ...initialState.requestData };
    },
    setPage: (state, action: PayloadAction<Paging>) => {
      state.requestData.paging.page = action.payload.page;
      state.requestData.paging.pageSize = action.payload.pageSize;
    },
    setFilters: (state, action: PayloadAction<GetPublicJobBoardResourcesTableFilters>) => {
      state.requestData.filters = { ...action.payload };
      state.requestData.paging = initialState.requestData.paging;
    },
    resetTableData: (state) => {
      state.data.tableData = initialState.data.tableData;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getHubPosts.pending, (state) => {
      state.data.tableDataIsLoading = true;
    });
    builder.addCase(getHubPosts.fulfilled, (state, action: PayloadAction<TableDataResponse<PublicJobBoardResourceTableView>>) => {
      state.data.tableDataIsLoading = false;
      state.data.tableData = action.payload;
      state.data.tableData.noResult = state.data.tableData.rows.length === 0;
    });
    builder.addCase(getHubPosts.rejected, (state) => {
      state.data.tableDataIsLoading = false;
    });

    builder.addCase(getPublicJobBoardResource.fulfilled, (state, action: PayloadAction<PublicJobBoardResourceTableView>) => {
      state.data.selectedResource = action.payload;
    });
  },
});

export const { resetRequestData, setPage, setFilters, resetTableData } = hubPostsSlice.actions;
export const hubPostsReducer = hubPostsSlice.reducer;
